import DarkButton from "components/actions/DarkButton";
import InputField from "components/fields/InputField";
import BackIcon from "components/icons/BackIcon";
import { useState } from "react";
import CheckIcon from "components/icons/Check";
import WhiteButton from "components/actions/WhiteButton";
import AtriumApi from "api";
import AlertPane from "components/alert";
import { useNavigate, useSearchParams } from "react-router-dom";
import Avatar from "components/avatar";
import NewChildForm from "../register/new-child-form";
import CheckInCode from "./qrcode";

const CheckIn = () => {
  const [step, setStep] = useState("init");
  const [selected, setSelected] = useState([]);
  const [parent, setParent] = useState({});
  const [kids, setKids] = useState([]);

  const [queryParams] = useSearchParams();

  const [phone, setPhone] = useState(queryParams.get("phone") || "");
  const [error, setError] = useState("");
  const [errors, setErrors] = useState([]);
  const [checkinCode, setCheckinCode] = useState("");

  queryParams.set("phone", "");
  const navigate = useNavigate();

  const verifyPhone = (e) => {
    setError("");
    if (!phone) {
      return setError("Phone number is required");
    }
    e.target.setAttribute("disabled", "");
    AtriumApi.post("/v1/check", { phone: phone })
      .then((resp) => {
        e.target.removeAttribute("disabled");
        setKids(resp.data.children);
        setParent(resp.data.user);
        setStep("pick-kids");
      })
      .catch((error) => {
        e.target.removeAttribute("disabled");
        if (error.response?.status === 404) {
          navigate(`/member/register?phone=${phone}`);
        } else {
          throw error;
        }
      });
  };

  const toggleSelection = (kid) => {
    const index = selected.indexOf(kid.id);
    if (index === -1) {
      setSelected(selected.concat([kid.id]));
    } else {
      setSelected(selected.slice(0, index).concat(selected.slice(index + 1)));
    }
  };

  const setupCheckIn = () => {
    AtriumApi.post("/v1/check-in", {
      guardianId: parent.id,
      children: selected,
    }).then((resp) => {
      setCheckinCode(resp.data.code);
      setStep("notice");
    });
  };

  const onSubmit = (user) => {
    setErrors([]);
    const formData = new FormData();
    for (const key in user) {
      formData.append(key, user[key]);
    }
    formData.append("related", parent.phone);
    AtriumApi.post("/v1/users/register", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }).then((resp) => {
      if (resp.status === 422) {
        setErrors(resp.data.errors);
        return;
      }
      setKids([...kids.filter((x) => x.id !== resp.data.id), resp.data]);
      setStep("pick-kids");
    });
  };

  return (
    <>
      {error ? (
        <div className="mt-3">
          <AlertPane state="error">{error}</AlertPane>
        </div>
      ) : (
        ""
      )}
      {step === "init" && (
        <div className="flex flex-1 h-full flex-row items-center">
          <div className="flex-1 flex flex-col gap-6 px-4 mb-16">
            <p className="text-2xl font-bold dark:text-white">
              Please enter your phone number to check-in.
            </p>
            <InputField
              placeholder="Phone number"
              type="text"
              value={phone}
              state={error ? "error" : ""}
              onChange={(e) => setPhone(e.target.value)}
            />
            <DarkButton onClick={verifyPhone}>Continue</DarkButton>
          </div>
        </div>
      )}

      {step === "pick-kids" && (
        <div className="flex flex-col gap-6 px-4 py-2">
          <div>
            <button
              onClick={() => setStep("init")}
              className="bg-white text-navy-900 px-4 py-3 rounded-xl"
            >
              <BackIcon />
            </button>
          </div>
          <p className="text-3xl font-bold dark:text-white">
            Please select kid(s) to check in.
          </p>
          <div className="flex flex-col gap-2">
            {kids.map((kid, i) => (
              <div
                key={"kid" + i}
                onClick={(e) => toggleSelection(kid)}
                className={`bg-white select-none border-2 ${
                  selected.indexOf(kid.id) === -1
                    ? "border-[transparent]"
                    : "border-brand-500"
                } hover:cursor-pointer dark:bg-navy-700 flex flex-row items-center p-2 rounded-xl gap-6`}
              >
                <Avatar user={kid} />
                <div className="flex-1">
                  <p className="font-bold text-normal mb-1">
                    {kid.firstName} {kid.lastName}
                  </p>
                  <p className="text-gray-700 text-sm">
                    {new Date().getFullYear() - new Date(kid.dob).getFullYear()}{" "}
                    years
                  </p>
                </div>
                <div
                  className={`w-8 ${
                    selected.indexOf(kid.id) === -1 ? "hidden" : "block"
                  }`}
                >
                  <span className="inline-block p-1 rounded-full bg-brand-500 text-white">
                    <CheckIcon />
                  </span>
                </div>
              </div>
            ))}
          </div>

          <div className="flex flex-col gap-4">
            <DarkButton
              onClick={() => setupCheckIn()}
              disabled={selected.length === 0}
            >
              {selected.length === 0
                ? "Please select at least 1 kid"
                : `Check-in ${selected.length} kid${
                    selected.length > 1 ? "s" : ""
                  }`}
            </DarkButton>
            <WhiteButton onClick={() => setStep("add-child")}>
              Add guest
            </WhiteButton>
          </div>
        </div>
      )}

      {step === "add-child" && (
        <div className="flex flex-col gap-6 px-4 py-2">
          <div>
            <button
              onClick={() => navigate(`/member/check-in?phone=${phone}`)}
              className="bg-white text-navy-900 px-4 py-3 rounded-xl"
            >
              <BackIcon />
            </button>
          </div>
          <p className="text-3xl font-bold">Add child/guest</p>
          {errors.length ? (
            <AlertPane state="error">
              Sorry, we have trouble to save your changes
            </AlertPane>
          ) : (
            ""
          )}

          <NewChildForm onSubmit={onSubmit} errors={errors} />
        </div>
      )}

      {step === "notice" && (
        <div className="flex-1 flex flex-col gap-6 px-4 py-2">
          <div className="">
            <button
              onClick={() => setStep("pick-kids")}
              className="bg-white text-navy-900 px-4 py-3 rounded-xl"
            >
              <BackIcon />
            </button>
          </div>
          <CheckInCode parent={parent} checkinCode={checkinCode} />
        </div>
      )}
    </>
  );
};

export default CheckIn;
